import { getChainInfo } from '@spritz-finance/sdk'
import { Body, Button, colors, Dialog, Icon, Margin, OpenNewSvg, Spacer } from '@spritz-finance/ui'

import dayjs from 'dayjs'
import React, { useState } from 'react'
import styled from 'styled-components'
import { ExpandDetailIcon } from '../../../../components/ExpandedDetailIcon'
import { FadePresence } from '../../../../components/FadePresence'
import { Column } from '../../../../components/Layout/Column'
import { Row } from '../../../../components/Layout/Row'
import { formatCurrency } from '../../../../utils/format'
import { useConfetti } from '../../../../utils/useConfetti'
import { NetworkLogo } from '../../../../web3/networkSwitcher/NetworkLogo'
import { useWeb3Connection } from '../../../../web3/Web3Provider'
import { DirectPayType, useDirectPayContext } from './context/DirectPayContext'
import { TokenImage } from './TokenListItem'
import { BalanceRow } from '../../../spritzCard/SpritzCardInfo'
import { CheckbookVirtualCard } from '../../../../graphql/generated/graphql'

const Banner = styled.div`
    padding: ${Margin.Small};
    background-color: var(--surface-3, ${colors.ltAqua[25]});
    border-radius: 10px;
`

export const SummaryContainer = styled.div`
    padding ${Margin.Med};
`

export const SummaryWrapper = styled.div`
    padding ${Margin.Small};
    border-radius: 10px;
    border: 1px solid #D6D6D6;
`

const UpdatedCardBalance = () => {
    const { fullAccount } = useDirectPayContext()

    // cache is updated to display new balance
    const newBalance = (fullAccount as CheckbookVirtualCard).balance ?? 0

    return (
        <>
            <Spacer height={24} />
            <BalanceRow>
                <Body.Med size={14}>New balance</Body.Med>
                <Body.Bold size={14}>{formatCurrency(newBalance)}</Body.Bold>
            </BalanceRow>
        </>
    )
}

const SummaryBanner = () => {
    const { type } = useDirectPayContext()
    let title, subtitle
    switch (type) {
        case DirectPayType.SpritzCard:
            title = 'Funds have been added to your SpritzCard!'
            subtitle =
                'Your crypto is ready to spend! Use your Spritz Card anywhere that accepts Visa virtual cards.'
            break
        case DirectPayType.OffRamp:
            title = 'Transfer sent!'
            subtitle =
                'Your transfer is on its way to the bank! It can take up to 4-5 business days for it to reflect in the account balance.'
            break
        case DirectPayType.BillPay:
            title = 'Payment sent!'
            subtitle =
                'Your payment is on its way to your billing account! It can take up to 3-4 business days for it to reflect in your account balance.'
            break
    }

    return (
        <Banner>
            <Column align="center">
                <Body.SmBold size={16}>{title}</Body.SmBold>
                <Spacer height={16} />
                <Body.Med size={14} align="center" style={{ maxWidth: 516 }}>
                    {subtitle}
                </Body.Med>
            </Column>
        </Banner>
    )
}

const ExpandedAmountsWrapper = styled.div`
    margin-bottom: 12px;
    border-top: 1px solid var(--surface-3, ${colors.ltGrey});
    border-bottom: 1px solid var(--surface-3, ${colors.ltGrey});
    padding: 10px 16px;
`

export const DirectPaySummary: React.VFC = () => {
    const { connectedNetwork } = useWeb3Connection()
    const { directPayment, closeDialog, receipt, selectedToken, type } = useDirectPayContext()
    const [expandedAmounts, setExpandedAmounts] = useState(false)

    useConfetti()

    if (!directPayment) return null
    const chainInfo = connectedNetwork ? getChainInfo(connectedNetwork) : null
    const isCard = type === DirectPayType.SpritzCard

    return (
        <>
            <Dialog.Title>Payment Summary</Dialog.Title>
            <SummaryContainer>
                <SummaryBanner />
                <Spacer height={24} />
                <SummaryWrapper>
                    <Row align="space-between">
                        <Body.SmBold size={14}>Paid to:</Body.SmBold>
                        <Body.Med size={14}>{directPayment.account.name}</Body.Med>
                    </Row>
                    <Spacer height={16} />
                    <div
                        onClick={() => setExpandedAmounts(!expandedAmounts)}
                        style={{ cursor: 'pointer' }}
                    >
                        <Row align="space-between">
                            <Body.SmBold size={14}>Total payment amount:</Body.SmBold>
                            <Row>
                                <Body.Med size={14}>
                                    {formatCurrency(directPayment.totalAmount)}
                                </Body.Med>
                                <ExpandDetailIcon active={expandedAmounts} />
                            </Row>
                        </Row>
                    </div>

                    <Spacer height={16} />

                    <FadePresence renderKey={expandedAmounts}>
                        {expandedAmounts && (
                            <ExpandedAmountsWrapper>
                                {!!directPayment.rewardsAmount && (
                                    <>
                                        <Row align="space-between">
                                            <Body.SmBold size={14}>Rewards applied:</Body.SmBold>
                                            <Body.Med size={14}>
                                                {formatCurrency(directPayment.rewardsAmount)}
                                            </Body.Med>
                                        </Row>
                                        <Spacer height={16} />
                                    </>
                                )}
                                {!!directPayment.feeAmount && (
                                    <>
                                        <Row align="space-between">
                                            <Body.SmBold size={14}>Spritz fee:</Body.SmBold>
                                            <Body.Med size={14}>
                                                {formatCurrency(directPayment.feeAmount)}
                                            </Body.Med>
                                        </Row>
                                        <Spacer height={16} />
                                    </>
                                )}
                                <Row align="space-between">
                                    <Body.SmBold size={14}>You paid:</Body.SmBold>
                                    <Body.Bold size={14}>
                                        {formatCurrency(directPayment.amountDue)}
                                    </Body.Bold>
                                </Row>
                            </ExpandedAmountsWrapper>
                        )}
                    </FadePresence>

                    <Row align="space-between">
                        <Body.SmBold size={14}>Token used:</Body.SmBold>
                        <div style={{ lineHeight: '18px', height: '18px', display: 'flex' }}>
                            <TokenImage
                                src={selectedToken?.tokenImageUrl}
                                style={{ height: '18px', width: '18px', marginRight: '3px' }}
                            />
                            <Body.Med size={14}>{selectedToken?.symbol}</Body.Med>
                        </div>
                    </Row>
                    <Spacer height={16} />
                    {chainInfo?.name && connectedNetwork && (
                        <>
                            <Row align="space-between">
                                <Body.SmBold size={14}>Network:</Body.SmBold>
                                <Row>
                                    <NetworkLogo network={connectedNetwork} size={14} />
                                    <Spacer width={4} />
                                    <Body.Med size={14}>{chainInfo.name}</Body.Med>
                                </Row>
                            </Row>
                            <Spacer height={16} />
                        </>
                    )}
                    <Row align="space-between">
                        <Body.SmBold size={14}>Transaction date:</Body.SmBold>
                        <Body.Med size={14}>{dayjs(new Date()).format('MMM DD, YYYY')}</Body.Med>
                    </Row>
                </SummaryWrapper>
                <Spacer height={16} />
                <Row align="center">
                    <Button.Text
                        onClick={() => {
                            if (chainInfo) {
                                window.open(
                                    chainInfo.getExplorerTransactionLink(receipt!.transactionHash)
                                )
                            }
                        }}
                    >
                        <Row>
                            <Body.Med size={12}>View transaction on the blockchain</Body.Med>{' '}
                            <Spacer width={8} />
                            <Icon icon={OpenNewSvg} />
                        </Row>
                    </Button.Text>
                </Row>

                {isCard && <UpdatedCardBalance />}

                <Spacer height={24} />
                <Row align="center">
                    <Button.CTA onClick={closeDialog}>Done</Button.CTA>
                </Row>
                <Spacer height={32} />
            </SummaryContainer>
        </>
    )
}
